/**
 * Shared Safety dashboard preset — the widget catalog, data fetchers, filter
 * dimensions, and role-based default layouts used by both the Safety Overview
 * page and the unified Home dashboard. Single source of truth so the two
 * surfaces never drift.
 */
import axios from 'axios';
import { fetchYoY, fetchComparison } from '../api/client';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// KPI group organized into labeled, color-coded sections for a scannable
// layout (instead of one flat grid of ~24 cards).
export const SAFETY_KPI_SECTIONS = [
  {
    label: 'Frequency Rates', accent: 'blue',
    metrics: ['safety.trir', 'safety.ltifr', 'safety.lwc_rate', 'safety.mtc_rate', 'safety.near_miss_rate'],
  },
  {
    label: 'Incidents', accent: 'indigo',
    metrics: ['safety.incidents.open', 'safety.incidents.draft', 'safety.incidents.total',
      'safety.incidents.overdue', 'safety.incidents.pending_approval',
      'safety.incidents.high_severity', 'safety.incidents.critical', 'safety.near_miss.total',
      'safety.incidents.fatality', 'safety.incidents.injuries'],
  },
  {
    label: 'CAPA', accent: 'amber',
    metrics: ['safety.capa.open', 'safety.capa.overdue', 'safety.capa.pending_approval', 'safety.capa.closure_rate'],
  },
  {
    label: 'Leading Indicators', accent: 'teal',
    // safety.ssem.maturity is deliberately absent: the hero row already renders
    // SSEM alongside PSS and Top Open Risks, so carrying it here too showed the
    // same score twice on one dashboard. The metric stays registered — the hero
    // widget reads it directly.
    metrics: ['safety.observations.total', 'safety.observations.unsafe_acts',
      'safety.observations.unsafe_conditions', 'safety.hazards.high_risk_open'],
  },
  {
    label: 'Audit', accent: 'purple',
    metrics: ['safety.audit.compliance_pct', 'safety.audit.critical_findings'],
  },
];

// Flat list (for export + comparison metric args) derived from the sections.
export const SAFETY_KPI_METRICS = SAFETY_KPI_SECTIONS.flatMap((s) => s.metrics);

// Backlog strip for the Home landing page: the things somebody has to ACT on.
// Every metric here is point-in-time in the registry (date_column=None), so the
// period filter does not apply to it — "4 overdue CAPAs" means four, full stop.
// Tone is the ALERT colour used only when the count is above zero.
export const SAFETY_ATTENTION_ITEMS = [
  { metric: 'safety.incidents.overdue', label: 'Overdue incidents', tone: 'critical' },
  { metric: 'safety.capa.overdue', label: 'Overdue CAPAs', tone: 'critical' },
  { metric: 'safety.hazards.high_risk_open', label: 'High-risk hazards', tone: 'critical' },
  { metric: 'safety.incidents.pending_approval', label: 'Awaiting approval', tone: 'warning' },
  { metric: 'safety.capa.due_soon', label: 'CAPAs due in 30d', tone: 'warning' },
  { metric: 'safety.incidents.open', label: 'Open incidents', tone: 'info' },
];

// Curated headline KPIs for the LEAN Home default — a scannable executive set
// instead of all ~24 cards. The full sections still live on the Safety Overview
// page and can be added to Home via Customize.
//
// Deliberately holds NO metric that the attention strip above already shows.
// The previous set repeated all six of its own tiles in the KPI group below it,
// which is a large part of why the landing page read as "too much data".
// What is left is performance — rates and reporting culture — which is a
// different question from "what is on my desk".
export const SAFETY_HEADLINE_METRICS = [
  'safety.trir',
  'safety.ltifr',
  'safety.near_miss_rate',
  'safety.observations.total',
];

// Heinrich Safety Triangle bands, apex (most severe lagging outcome) → base
// (leading indicators). Each maps to a count metric that drills into its
// records. Colours run a red→green gradient; `group` drives the side brackets.
// Defined before SAFETY_EXPORT_SPEC so the export's pyramid chart can reuse it.
export const SAFETY_PYRAMID_TIERS = [
  { metric: 'safety.incidents.fatality', label: 'Fatality', color: '#c0161c', textColor: '#ffffff', group: 'lagging' },
  { metric: 'safety.incidents.lti', label: 'Major / Lost Time', color: '#e5484d', textColor: '#ffffff', group: 'lagging' },
  { metric: 'safety.incidents.mtc', label: 'Medical Treatment', color: '#f4837c', group: 'lagging' },
  { metric: 'safety.incidents.first_aid', label: 'First Aid', color: '#fbd0cb', group: 'lagging' },
  { metric: 'safety.near_miss.total', label: 'Near Miss', color: '#d4edcf', group: 'leading' },
  { metric: 'safety.observations.unsafe_conditions', label: 'Unsafe Condition', color: '#9ed19b', group: 'leading' },
  { metric: 'safety.observations.unsafe_acts', label: 'Unsafe Act', color: '#5fa85c', group: 'leading' },
];

// Export report spec — what the PDF/Excel/CSV includes: grouped KPI sections
// (accent-coloured cards), the incident trend line chart, the Heinrich pyramid
// triangle, dimension breakdown charts, and a per-site comparison chart. In the
// PDF these render as embedded chart images (mirroring the on-screen dashboard);
// CSV/Excel fall back to the equivalent data tables.
export const SAFETY_EXPORT_SPEC = {
  sections: SAFETY_KPI_SECTIONS.map((s) => ({ label: s.label, accent: s.accent, metrics: s.metrics })),
  trend: { title: 'Incident Trend (last 12 months)', months: 12 },
  pyramid: { title: 'Heinrich Safety Triangle', tiers: SAFETY_PYRAMID_TIERS },
  breakdowns: [
    { title: 'Open Incidents by Department', metric: 'safety.incidents.open', group_by: 'department' },
    { title: 'Incidents by Status', metric: 'safety.incidents.total', group_by: 'status' },
    { title: 'CAPAs by Status', metric: 'safety.capa.total', group_by: 'status' },
  ],
  comparison_metrics: ['safety.trir', 'safety.ltifr', 'safety.lwc_rate', 'safety.near_miss_rate'],
};

// Maps a breakdown widget id → its export breakdown block, so the PDF includes
// only the breakdown charts actually on the dashboard.
const _BREAKDOWN_BY_ID = {
  'open-by-dept': { title: 'Open Incidents by Department', metric: 'safety.incidents.open', group_by: 'department' },
  'incidents-by-status': { title: 'Incidents by Status', metric: 'safety.incidents.total', group_by: 'status' },
  'incidents-by-site': { title: 'Incidents by Site', metric: 'safety.incidents.total', group_by: 'site' },
  'incidents-by-reporter': { title: 'Incidents by Reporter', metric: 'safety.incidents.total', group_by: 'reporter' },
  'incidents-by-category': { title: 'Incidents by Category', metric: 'safety.incidents.total', group_by: 'incident_category' },
  'incidents-by-severity': { title: 'Incidents by Severity', metric: 'safety.incidents.total', group_by: 'severity' },
  'incidents-by-location': { title: 'Incidents by Location', metric: 'safety.incidents.total', group_by: 'location' },
  // Stacked pivot on screen; exported as an incidents-by-department bar.
  'type-by-dept': { title: 'Incident Type by Department', metric: 'safety.incidents.total', group_by: 'department' },
};

/**
 * Build the export spec from the widgets CURRENTLY VISIBLE on the dashboard, so
 * the PDF/Excel/CSV mirrors what's on screen (respecting the lean default,
 * role-scoping, and per-user Customize choices). Falls back to the full spec if
 * no widget list is supplied.
 */
export function safetyExportSpecForWidgets(visibleIds) {
  if (!Array.isArray(visibleIds) || visibleIds.length === 0) return SAFETY_EXPORT_SPEC;
  const set = new Set(visibleIds);
  const spec = {};

  // KPI cards: full sections if the full KPI widget is shown, else the lean
  // headline strip if that's what's on the dashboard.
  const sections = [];
  // The backlog strip leads the dashboard, so it leads the report too. These
  // are point-in-time metrics — the backend ignores the period for them, which
  // is why the block can sit alongside period-scoped KPIs without contradiction.
  if (set.has('attention')) {
    sections.push({ label: 'Needs Attention', accent: 'red',
      metrics: SAFETY_ATTENTION_ITEMS.map((i) => i.metric) });
  }
  if (set.has('kpis')) {
    sections.push(...SAFETY_KPI_SECTIONS.map((s) => ({ label: s.label, accent: s.accent, metrics: s.metrics })));
  } else if (set.has('kpis-headline')) {
    sections.push({ label: 'Headline KPIs', accent: 'blue', metrics: SAFETY_HEADLINE_METRICS });
  }
  if (sections.length) spec.sections = sections;

  if (set.has('incident-trend')) spec.trend = { title: 'Incident Trend (last 12 months)', months: 12 };
  if (set.has('incident-yoy')) {
    spec.yoy = { title: 'Incidents by Month — Year on Year', metric: 'safety.incidents.total', num_years: 3 };
  }
  if (set.has('pyramid')) spec.pyramid = { title: 'Heinrich Safety Triangle', tiers: SAFETY_PYRAMID_TIERS };

  const breakdowns = Object.entries(_BREAKDOWN_BY_ID)
    .filter(([id]) => set.has(id))
    .map(([, b]) => b);
  if (breakdowns.length) spec.breakdowns = breakdowns;

  // Grouped per-site comparisons (each an on-screen "by site" chart).
  const comparisons = [];
  if (set.has('site-comparison')) {
    comparisons.push({ title: 'Site Comparison', metrics: ['safety.trir', 'safety.ltifr', 'safety.lwc_rate', 'safety.near_miss_rate'] });
  }
  if (set.has('accident-by-site')) {
    comparisons.push({ title: 'Accident Summary (by site)', metrics: ACCIDENT_SITE_METRICS.map((m) => m.key) });
  }
  if (comparisons.length) spec.comparisons = comparisons;

  spec.metrics = sections.flatMap((s) => s.metrics);
  return spec;
}

// Companion by-site charts (mirror the sample's Accident / Incident summaries).
// Each is a grouped bar chart: x = site, one series per metric. Uses the
// cross-site /v2/comparison endpoint (respects time + dimension filters).
export const ACCIDENT_SITE_METRICS = [
  { key: 'safety.incidents.fatality', label: 'Fatality' },
  { key: 'safety.incidents.lti', label: 'Major / Lost Time' },
  { key: 'safety.incidents.mtc', label: 'Medical Treatment' },
  { key: 'safety.incidents.first_aid', label: 'First Aid' },
];
// Build a fetcher that shapes /v2/comparison into grouped-bar data. recharts
// dataKeys can't contain dots (treated as nested paths), so metrics are aliased
// to m0/m1/… and the human label is carried on the series.
function makeSiteComparisonFetcher(metricDefs) {
  return async ({ time, dimensions, standard, signal }) => {
    const body = { module: 'safety', metrics: metricDefs.map((m) => m.key) };
    if (time) body.time = time;
    if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
    if (standard) body.standard = standard;
    const resp = await fetchComparison(body, { signal });
    const cols = resp?.metrics || [];
    const sites = resp?.sites || [];
    const series = cols.map((c, i) => ({
      key: `m${i}`,
      label: metricDefs.find((m) => m.key === c.key)?.label || c.label || c.key,
      metricKey: c.key,
    }));
    const data = sites.map((s) => {
      const row = { site: s.site_name };
      series.forEach((sd) => { row[sd.key] = Number(s.values?.[sd.metricKey] ?? 0); });
      return row;
    });
    return { data, series: series.map(({ key, label }) => ({ key, label })) };
  };
}

const accidentBySiteFetcher = makeSiteComparisonFetcher(ACCIDENT_SITE_METRICS);

export const SAFETY_DIMENSIONS = [
  { key: 'department', label: 'Department' },
  { key: 'status', label: 'Status' },
  { key: 'severity', label: 'Severity' },
  { key: 'priority', label: 'Priority' },
];

// Rolling 12-month incident trend (legacy safety endpoint; counts on one axis).
async function trendFetcher({ siteId, signal }) {
  const params = new URLSearchParams({ months: '12' });
  if (siteId) params.set('site_id', siteId);
  const { data } = await axios.get(`${API}/dashboard/safety/trends?${params.toString()}`, { signal });
  const rows = (data?.trends || []).map((t) => ({
    period: t.period,
    recordable: t.recordable_incidents,
    near_miss: t.near_misses,
    lti: t.lost_time_injuries,
  }));
  return {
    data: rows,
    series: [
      { key: 'recordable', label: 'Recordable' },
      { key: 'near_miss', label: 'Near Miss' },
      { key: 'lti', label: 'Lost Time' },
    ],
  };
}

// Rolling 12-month KPI *rate* trend (TRIR/LTIFR/LWC/MTC) from the same trends
// endpoint — migrated from Incident Intelligence's "KPI Trends" chart. All are
// rates so they share one y-axis (matches the legacy single-axis line chart).
async function rateTrendFetcher({ siteId, signal }) {
  const params = new URLSearchParams({ months: '12' });
  if (siteId) params.set('site_id', siteId);
  const { data } = await axios.get(`${API}/dashboard/safety/trends?${params.toString()}`, { signal });
  const rows = (data?.trends || []).map((t) => ({
    period: t.period,
    trir: t.trir,
    ltifr: t.ltifr,
    lwc_rate: t.lwc_rate,
    near_miss_rate: t.near_miss_rate,
  }));
  return {
    data: rows,
    series: [
      { key: 'trir', label: 'TRIR' },
      { key: 'ltifr', label: 'LTIFR' },
      { key: 'lwc_rate', label: 'LWC Rate' },
      { key: 'near_miss_rate', label: 'Near Miss Rate' },
    ],
  };
}

async function yoyIncidentsFetcher({ siteId, dimensions, signal }) {
  const body = { module: 'safety', metric: 'safety.incidents.total', num_years: 3 };
  if (siteId) body.site_id = siteId;
  if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
  const resp = await fetchYoY(body, { signal });
  return { data: resp.data, series: resp.series };
}

/** All available widgets for the safety dashboards. */
export function buildSafetyCatalog() {
  return [
    { id: 'kpis', type: 'kpi-group', colSpan: 2, title: 'Key KPIs',
      props: { sections: SAFETY_KPI_SECTIONS, columns: 4 } },
    // Lean headline KPI strip (curated performance metrics) for the Home default.
    { id: 'kpis-headline', type: 'kpi-group', colSpan: 2, title: 'Headline KPIs',
      props: { metrics: SAFETY_HEADLINE_METRICS, columns: 4 } },
    // Backlog strip — what needs acting on, quiet when there is nothing.
    { id: 'attention', type: 'attention', colSpan: 2, title: 'Needs Attention',
      props: { items: SAFETY_ATTENTION_ITEMS, title: 'Needs attention' } },
    // Hero row: PSS + SSEM + Top Open Risks across, in one full-width widget.
    // (PSS / SSEM / Top Risks are composed inside the hero, not separate cards.)
    { id: 'hero', type: 'safety-hero', colSpan: 2, title: 'Overview (PSS · SSEM · Top Risks)', props: {} },
    { id: 'ai-summary', type: 'ai-summary', colSpan: 2, title: 'AI Safety Summary', props: {} },
    { id: 'my-work', type: 'supervisor', colSpan: 2, title: 'My Work (personal)', props: {} },
    { id: 'incident-trend', type: 'chart', colSpan: 2, title: 'Incident Trend (last 12 months)',
      props: { title: 'Incident Trend (last 12 months)', chart: { type: 'line', xKey: 'period', height: 320 }, fetcher: trendFetcher } },
    { id: 'incident-yoy', type: 'chart', colSpan: 2, title: 'Incidents by Month — Year on Year',
      props: { title: 'Incidents by Month — Year on Year', chart: { type: 'line', xKey: 'month', height: 320 }, fetcher: yoyIncidentsFetcher } },
    { id: 'open-by-dept', type: 'breakdown', colSpan: 1, title: 'Open Incidents by Department',
      props: { title: 'Open Incidents by Department', metric: 'safety.incidents.open', groupBy: 'department' } },
    { id: 'incidents-by-status', type: 'breakdown', colSpan: 1, title: 'Incidents by Status',
      props: { title: 'Incidents by Status', metric: 'safety.incidents.total', groupBy: 'status' } },
    { id: 'incidents-by-site', type: 'breakdown', colSpan: 1, title: 'Incidents by Site',
      props: { title: 'Incidents by Site', metric: 'safety.incidents.total', groupBy: 'site' } },
    { id: 'incidents-by-reporter', type: 'breakdown', colSpan: 1, title: 'Incidents by Reporter (user-wise)',
      props: { title: 'Incidents by Reporter (user-wise)', metric: 'safety.incidents.total', groupBy: 'reporter' } },
    // Single-dimension breakdowns migrated from Incident Intelligence.
    { id: 'incidents-by-category', type: 'breakdown', colSpan: 1, title: 'Incidents by Category',
      props: { title: 'Incidents by Category', metric: 'safety.incidents.total', groupBy: 'incident_category', chartType: 'hbar' } },
    { id: 'incidents-by-severity', type: 'breakdown', colSpan: 1, title: 'Incidents by Severity',
      props: { title: 'Incidents by Severity', metric: 'safety.incidents.total', groupBy: 'severity', chartType: 'pie' } },
    { id: 'incidents-by-location', type: 'breakdown', colSpan: 1, title: 'Incidents by Location',
      props: { title: 'Incidents by Location', metric: 'safety.incidents.total', groupBy: 'location', chartType: 'hbar' } },
    // Stacked pivots (Intelligence cross-dimension): type × department, type × location.
    { id: 'type-by-dept', type: 'breakdown', colSpan: 2, title: 'Incident Type by Department',
      props: { title: 'Incident Type by Department', metric: 'safety.incidents.total', groupBy: 'department', stackBy: 'incident_category' } },
    { id: 'type-by-location', type: 'breakdown', colSpan: 2, title: 'Incident Type by Location',
      props: { title: 'Incident Type by Location', metric: 'safety.incidents.total', groupBy: 'location', stackBy: 'incident_category' } },
    // KPI rate trend (Intelligence "KPI Trends"): TRIR/LTIFR/LWC/near-miss by month.
    { id: 'rate-trend', type: 'chart', colSpan: 2, title: 'KPI Rate Trend (last 12 months)',
      props: { title: 'KPI Rate Trend (last 12 months)', chart: { type: 'line', xKey: 'period', height: 320 }, fetcher: rateTrendFetcher } },
    // Corporate site-comparison table (rate KPIs per site).
    { id: 'site-comparison', type: 'comparison-table', colSpan: 2, title: 'Site Comparison',
      props: { title: 'Site Comparison', metrics: ['safety.trir', 'safety.ltifr', 'safety.lwc_rate', 'safety.near_miss_rate'] } },
    // Corporate: workforce summary + high-risk-site ranking.
    { id: 'workforce', type: 'workforce', colSpan: 2, title: 'Workforce Summary', props: {} },
    { id: 'high-risk-sites', type: 'high-risk-sites', colSpan: 1, title: 'High Risk Sites', props: {} },
    // Heinrich Safety Triangle — severity bands with click-to-drill + ratio insights.
    { id: 'pyramid', type: 'pyramid', colSpan: 1, title: 'Heinrich Safety Triangle',
      props: { title: 'Heinrich Safety Triangle', tiers: SAFETY_PYRAMID_TIERS } },
    // Companion by-site summaries (grouped bars) that pair with the triangle.
    { id: 'accident-by-site', type: 'chart', colSpan: 1, title: 'Accident Summary (by site)',
      props: { title: 'Accident Summary (by site)', chart: { type: 'bar', xKey: 'site', height: 300 }, fetcher: accidentBySiteFetcher } },
  ];
}

// Widgets available to EVERY role (personal / self-scoped). All other widgets
// (analytics: KPIs, pyramid, trends, breakdowns, comparison, hero) are
// management-only — see safetyDashboardAccess / safetyCatalogFor.
const PERSONAL_WIDGET_IDS = new Set(['my-work']);

/**
 * Permission-scoped catalog: `analytics:read` gets the full analytics catalog;
 * everyone else gets only the personal widgets (My Work / My Tasks). This is
 * the widget-level guard — without that grant a user cannot even add an
 * analytics widget via Customize, which is why Customize can stay open to all.
 *
 * NOT the security boundary: /api/dashboard/v2 requires `analytics:read` AND
 * the metric's own resource permission on every metric, drill-down, breakdown
 * and export (see dashboard_framework._authorize_metric).
 */
export function safetyCatalogFor(access) {
  const full = buildSafetyCatalog();
  if (access?.analytics) return full;
  return full.filter((w) => PERSONAL_WIDGET_IDS.has(w.id));
}

const _SPAN2 = new Set(['hero', 'kpis', 'kpis-headline', 'attention', 'incident-trend', 'incident-yoy', 'ai-summary', 'my-work', 'type-by-dept', 'type-by-location', 'rate-trend', 'site-comparison', 'workforce']);
const _layout = (ids) => ids.map((id) => ({ id, colSpan: _SPAN2.has(id) ? 2 : 1 }));

// Safety Overview page default — the analytical set (no personal/legacy cards).
// Now also the migration target for Incident Intelligence + Corporate content:
// includes the category/severity/location breakdowns and site comparison.
export function safetyDefaultLayout() {
  return _layout([
    'kpis', 'workforce', 'pyramid', 'high-risk-sites',
    'incident-trend', 'rate-trend', 'incident-yoy',
    'site-comparison',
    'open-by-dept', 'incidents-by-status', 'incidents-by-site', 'incidents-by-reporter',
    'incidents-by-category', 'incidents-by-severity', 'incidents-by-location',
    'type-by-dept', 'type-by-location', 'accident-by-site',
  ]);
}

const _MGMT_ROLES = new Set([
  'Admin', 'admin', 'Super Admin', 'super_admin', 'Tenant Admin', 'tenant_admin',
  'Director', 'director', 'HSE Manager', 'hse_manager', 'Safety Manager', 'safety_manager',
  'c_suite', 'corporate_safety_head', 'group_admin',
]);

/**
 * SUPERSEDED by safetyDashboardAccess(). Kept because the role list is the
 * only record of who the dashboard used to be limited to; nothing calls it any
 * more. Safe to delete once you are happy with the permission-driven gate.
 */
export function isSafetyDashboardRole(role) {
  return _MGMT_ROLES.has(role);
}

// Roles the backend lets through every per-metric RBAC check
// (dashboard_framework._ADMIN_ROLES). Mirrored here so the UI does not hide a
// widget the server would happily serve them.
const _BYPASS_ROLES = new Set(['Admin', 'admin', 'group_admin']);

// A read on any of these means the user works with safety records, so the
// personal dashboard (My Work) is useful to them. The seeded Supervisor,
// Engineer and Operator roles are NOT granted `dashboards:read`, so without
// this floor the permission gate would take away the dashboard they have
// today — measured, not assumed: 3 live Supervisor users.
const _PERSONAL_FLOOR = [
  ['incidents', 'read'], ['observations', 'read'],
  ['capa', 'read'], ['hazards', 'read'],
];

/**
 * What this user may do on a safety dashboard, from their PERMISSIONS rather
 * than their role string.
 *
 * The role list this replaces contradicted the grants the tenant already had:
 * `manager` and `safety_officer` both hold `analytics:read` and still saw
 * nothing but My Work, purely because their role name was not in a hard-coded
 * set — and any tenant-defined role was locked out however it was configured.
 *
 * @param hasPermission (resource, action) => boolean, from usePermissions()
 * @param role          the user's role string, for the admin bypass only
 */
export function safetyDashboardAccess(hasPermission, role) {
  const bypass = _BYPASS_ROLES.has(role);
  const can = (r, a) => bypass || (typeof hasPermission === 'function' && hasPermission(r, a));

  const analytics = can('analytics', 'read');
  return {
    // The analytics widget catalog: KPIs, pyramid, trends, breakdowns,
    // comparisons — and the filter bar and saved views that drive them.
    analytics,
    // The personal dashboard. Anyone with analytics has it implicitly.
    personal: analytics || can('dashboards', 'read')
      || _PERSONAL_FLOOR.some(([r, a]) => can(r, a)),
    // The Export menu (PDF/Excel/CSV of the dashboard).
    exportReports: can('reports', 'export'),
    // Rearranging YOUR OWN dashboard is not a privilege: it changes nothing
    // about what data you can reach, only where your own widgets sit. Left
    // open to everyone by explicit decision, rather than gated on
    // `dashboards:configure` — which only 3 of 16 roles hold and which would
    // have taken Customize away from Safety Manager and Supervisor.
    customize: true,
  };
}

// ---------------------------------------------------------------------------
// Tile <-> route predicates for the Safety hub. Each surface's tile (SafetyHub)
// and its route guard (App.js) call the SAME function, so a tile can never lead
// to a screen that redirects straight back, and no-one with access is missing
// the tile.
//
// FINAL RULES
//   Executive Dashboard  : admin (isAdmin()) OR analytics:read
//   Supervisor Dashboard : admin OR analytics:read OR a supervisor/frontline
//                          role (SUPERVISOR_DASHBOARD_ROLES, normalised)
//   Workforce Data       : safety module AND role in WORKFORCE_ALLOWED_ROLES
//                          (exact mirror of the backend allow-list)
// ---------------------------------------------------------------------------

// users.role drifts between legacy title-case ('HSE Manager') and RBAC slugs
// ('hse_manager'); compare on one normalised form.
const _normRole = (role) => String(role || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

/**
 * `analytics:read`, evaluated exactly as App.js RequireResourcePermission
 * resource="analytics" action="read" evaluates it: admin bypass via isAdmin(),
 * else a RESOURCE-level grant (getResourcePermissions — NOT hasPermission(),
 * which also matches module_slug and so is wider than the route).
 *
 * @param perms the usePermissions() value ({ isAdmin, getResourcePermissions })
 */
export function canReadAnalytics(perms) {
  const { isAdmin, getResourcePermissions } = perms || {};
  if (typeof isAdmin === 'function' && isAdmin()) return true;
  return typeof getResourcePermissions === 'function'
    && getResourcePermissions('analytics').some((p) => p.action === 'read');
}

// Roles whose job the personal Supervisor ("My") dashboard serves even without
// analytics:read — the seeded Supervisor role does not hold it. Normalised copy
// of SUPERVISOR_DASH_ROLES in pages/dashboards/SupervisorDashboard.js; keep the
// two in step.
export const SUPERVISOR_DASHBOARD_ROLES = new Set([
  'supervisor',
  'admin', 'super_admin', 'tenant_admin',
  'hse_manager', 'safety_manager', 'manager', 'director', 'safety_officer',
  'contractor', 'worker', 'operator', 'technician', 'engineer',
]);

export function canAccessSupervisorDashboard(perms, role) {
  return canReadAnalytics(perms) || SUPERVISOR_DASHBOARD_ROLES.has(_normRole(role));
}

// MIRROR of backend/routes/workforce.py WORKFORCE_ALLOWED_ROLES — copied
// verbatim. Compared EXACTLY (not normalised) on purpose: the backend's
// _require_workforce_role is an exact `role not in` check, so a normalised
// match here would show the tile to e.g. 'Corporate Safety Head' and then 403.
// Change the backend list first, then this one.
export const WORKFORCE_ALLOWED_ROLES = new Set([
  'admin', 'Admin', 'super_admin', 'Super Admin',
  'tenant_admin', 'Tenant Admin',
  'safety_manager', 'Safety Manager',
  'hse_manager', 'HSE Manager',
  'corporate_safety_head',
  'group_admin',
  'c_suite',
  'director', 'Director',
]);

export function canAccessWorkforce(role) {
  return WORKFORCE_ALLOWED_ROLES.has(role || '');
}

/**
 * LEAN Home dashboard default — a landing page, not the analytical surface.
 *
 * It answers two questions in that order: what do I have to act on (the
 * attention strip), and how are we doing (four rates + the triangle + one
 * trend). Four widgets, ~one screen.
 *
 * - Users with `analytics:read`: attention → headline rates → triangle + trend,
 *   the last two side by side so they cost one row rather than two.
 * - Everyone else: only personal widgets (My Work / My Tasks). The analytics
 *   catalog is not available to them (see safetyCatalogFor).
 *
 * Deliberately NOT in the default, though all remain one click away under
 * Customize, and all stay on the Safety Overview page:
 * - `hero` (PSS · SSEM · Top Open Risks) — three cards that read "Insufficient
 *   Data" / "no data" / "0" until a tenant has months of history, which is a
 *   poor first impression for the width it occupies.
 * - `kpis` (the ~25-tile grouped set) — it repeated every headline metric.
 * - `incident-yoy` — a second line chart of the same incident counts as the
 *   trend chart directly above it.
 * Explicit colSpans so the pyramid pairs with the trend here without affecting
 * the Overview page, which sets its own.
 */
export function safetyDefaultLayoutFor(access) {
  if (access?.analytics) {
    return [
      { id: 'attention', colSpan: 2 },
      { id: 'kpis-headline', colSpan: 2 },
      { id: 'pyramid', colSpan: 1 },
      { id: 'incident-trend', colSpan: 1 },
    ];
  }
  // Non-management: personal work only.
  return [
    { id: 'my-work', colSpan: 2 },
  ];
}
